// Imports
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_SHIFT } from "../../constants/api";
import { checkUserPermissionFunc, formatTimeRangeToAMPM, truncateText } from "../../components/common/CommonFunctions";
import { Clock, Edit, Eye, EyeOff } from "lucide-react";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";
import { configBranchLabel } from "../../utils/branchSession";

import duration from "dayjs/plugin/duration";

// extend the plugin
dayjs.extend(duration);

// Module-level so their identity is stable: Reactable hashes the filter it is
// given, and a fresh object every render would refetch on every render.
const UNUSED_ONLY_FILTER = { unused_only: "true" };
const EMPTY_FILTER = {};

const EmployeeShift = () => {
    // States
    const [employeeShiftData, setEmployeeShiftData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    // FR-104: narrow the list to shifts nothing has used inside the configured
    // window. The window is a company setting; the rows carry it back so the
    // caption can say what "unused" actually measured rather than implying a
    // fixed 90 days.
    const [unusedOnly, setUnusedOnly] = useState(false);
    const [assignedOpen, setAssignedOpen] = useState(false);
    const [assignedShift, setAssignedShift] = useState(null);
    const [assignedEmployees, setAssignedEmployees] = useState([]);
    const [assignedLoading, setAssignedLoading] = useState(false);
    const [assignedError, setAssignedError] = useState("");
    const usageWindow = employeeShiftData?.[0]?.usage_window_days || null;
    const { fetch } = useApiMethods();

    const navigate = useNavigate();

    // Permissions
    const {
        add: addPermission,
        edit: editPermission,
        draft: draftPermission,
        active: isActive
    } = checkUserPermissionFunc("configuration", "Shift Configuration", "SubMenu");

    // Functions
    const handleAddShift = () => {
        navigate("/configuration/shiftconfiguration/add");
    };

    // Columns
    const employeeShiftColumns = [
        {
            accessor: "branch_name",
            Header: "Branch",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "code",
            Header: "Shift Code",
            sortable: true,
            filterable: true,
            width: '100px',
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "shift_name",
            Header: "Shift Name",
            sortable: true,
            filterable: true,
            width: "120px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "shift_timings",
            Header: "Shift Timings",
            sortable: true,
            Cell: ({ row }) => {
                const from = row?.shift_timings_from;
                const to = row?.shift_timings_to;
                return from && to ? (
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
                        {formatTimeRangeToAMPM(from, to, "–")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            accessor: "shift_hours",
            Header: "Shift Hours Summary",
            sortable: false,
            Cell: ({ row }) => {
                const breakHrs = row?.total_break_hrs;
                const shiftHrs = row?.total_shift_hrs;

                // helper function to format HH:mm:ss into "HH hrs mm mins"
                const formatDuration = (timeStr) => {
                    if (!timeStr) return "-";
                    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
                    const dur = dayjs.duration({
                        hours: hours || 0,
                        minutes: minutes || 0,
                        seconds: seconds || 0,
                    });

                    // if you prefer "08:30 hrs" instead of "08 hrs 30 mins"
                    return `${String(dur.hours()).padStart(2, "0")}:${String(
                        dur.minutes()
                    ).padStart(2, "0")} hrs`;
                };

                return (
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-medium text-gray-700 dark:text-gray-100">
                            {shiftHrs ? formatDuration(shiftHrs) : "-"}
                        </span>
                        {breakHrs && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-1">
                                (Break: {formatDuration(breakHrs)})
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "notes",
            Header: "Notes",
            sortable: true,
            Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : "-"
        },
        {
            accessor: "last_used",
            Header: "Last Used",
            sortable: true,
            Cell: ({ value, row }) => {
                // `is_unused` is null when usage could not be computed (a tenant
                // that has not run the roster migrations). Absent is not the
                // same as "unused" -- badging a shift dead on a failed lookup is
                // how somebody deletes one half a ward reports to.
                if (row?.is_unused === null || row?.is_unused === undefined) {
                    return <span className="text-xs text-muted-foreground">-</span>;
                }
                if (row.is_unused) {
                    return (
                        <div className="flex flex-col gap-0.5">
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-[2px] text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <EyeOff className="h-3 w-3" /> Unused
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                                {value ? `last used ${dayjs(value).format("DD MMM YYYY")}` : "never used"}
                            </span>
                        </div>
                    );
                }
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                            {value ? dayjs(value).format("DD MMM YYYY") : "In use"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            {row.employees_assigned
                                ? `${row.employees_assigned} employee${row.employees_assigned === 1 ? "" : "s"} assigned`
                                : `${row.usage_count || 0} use${row.usage_count === 1 ? "" : "s"}`}
                            {row.employees_assigned > 0 && (
                                <button
                                    type="button"
                                    title="View employees in this count"
                                    className="text-sidebar-primary hover:text-sidebar-primary/80"
                                    onClick={async (event) => {
                                        event.stopPropagation();
                                        setAssignedShift(row);
                                        setAssignedEmployees([]);
                                        setAssignedError("");
                                        setAssignedOpen(true);
                                        setAssignedLoading(true);
                                        try {
                                            const response = await fetch(EMPLOYEE_SHIFT, {
                                                id: row.id,
                                                assigned_employees: "true",
                                            });
                                            if (response?.status !== "success") {
                                                setAssignedError(response?.message || "Could not load employees for this shift.");
                                                setAssignedEmployees([]);
                                            } else {
                                                setAssignedEmployees(response?.data?.employees || []);
                                            }
                                        } catch (error) {
                                            setAssignedError("Could not load employees for this shift.");
                                            setAssignedEmployees([]);
                                        } finally {
                                            setAssignedLoading(false);
                                        }
                                    }}
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={`px-2 py-[2px] text-xs font-medium rounded-full ${value
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        }`}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission
            ? [
                {
                    accessor: "action",
                    Header: "Action",
                    sortable: false,
                    Cell: ({ row }) => {
                        const handleOpenEdit = () => {
                            navigate("/configuration/shiftconfiguration/add", {
                                state: { fromEdit: true, editDetails: row, isActive: isActive },
                            });
                        };

                        return (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                    onClick={handleOpenEdit}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];


    // UI
    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Clock}
                title="Employee Shift"
                subtitle={`Manage shifts for ${configBranchLabel()}. Timings saved here stay on this branch.`}
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={handleAddShift}
                buttonName="Add Shift"
            />

            <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
                <Button
                    variant={unusedOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUnusedOnly((prev) => !prev)}
                    className={cn("gap-1", unusedOnly && "bg-amber-600 hover:bg-amber-700 text-white")}
                    title="Shifts nothing has used inside the configured window"
                >
                    <EyeOff className="h-4 w-4" /> {unusedOnly ? "Showing unused only" : "Show unused only"}
                </Button>
                {usageWindow ? (
                    <span className="text-[11px] text-muted-foreground">
                        &ldquo;Unused&rdquo; means no roster, shift change, attendance or standing
                        assignment in the last {usageWindow} days. An administrator can change the
                        window under Settings &rsaquo; Configuration &rsaquo; Features &amp; Access.
                    </span>
                ) : null}
            </div>

            <Reactable
                columns={employeeShiftColumns}
                data={employeeShiftData}
                setData={setEmployeeShiftData}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_SHIFT}
                filter={unusedOnly ? UNUSED_ONLY_FILTER : EMPTY_FILTER}
                serverSide={true}
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
            />
            <DialogModal
                open={assignedOpen}
                onClose={() => setAssignedOpen(false)}
                title={assignedShift ? `${assignedShift.shift_name || "Shift"} — used employees` : "Used employees"}
                showActions
                hideSaveButton
                closeLabel="Close"
                width="420px"
            >
                <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {assignedLoading
                            ? "Loading employees..."
                            : `${assignedEmployees.length} employee${assignedEmployees.length === 1 ? "" : "s"}`}
                    </p>
                    {!assignedLoading && assignedEmployees.length > 0 && (
                        <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                            {assignedEmployees.map((employee) => (
                                <li key={`${employee.employee_code}-${employee.employee_name}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate" title={employee.employee_name}>{employee.employee_name}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{employee.employee_code}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {!assignedLoading && assignedError && (
                        <p className="text-sm text-red-600 dark:text-red-400">{assignedError}</p>
                    )}
                    {!assignedLoading && !assignedError && assignedEmployees.length === 0 && (
                        <p className="text-sm text-gray-500">No employees are assigned to this shift.</p>
                    )}
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default EmployeeShift;
